import { isEqual } from "lodash";

import {
  CALENDAR_NAVIGATION_SHIFT,
  CALENDAR_RANGE,
  INITIAL_CALENDAR_DROPDOWN_VALUE,
} from "../../constants";
import { TimeZoneEnum, TopCalendarMode } from "../../enums";
import { DatesUnit, RydeUiUnit } from "../../models";
import { sessionFirstStorageHelpers } from "../browser-storge";
import {
  endOfZonedCalendarDay,
  nowInTimezone,
  nowTDateISO,
  shiftZonedCalendarDate,
  startOfZonedCalendarDay,
} from "../date.helpers";

type CalendarShiftUnit =
  (typeof CALENDAR_NAVIGATION_SHIFT)[TopCalendarMode]["unit"];

const zonedNowISO = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).toISOString();

const LAST_MONTH_START = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).date(1).subtract(1, "month").toISOString();
const LAST_MONTH_END = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).date(1).subtract(1, "day").toISOString();

const LAST_30_DAYS_START = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .subtract(CALENDAR_RANGE.last30DaysOffset, "day")
    .toISOString();
const LAST_30_DAYS_END = (timezone: TimeZoneEnum): TDateISO =>
  zonedNowISO(timezone);

const TODAY_DATE = (timezone: TimeZoneEnum): TDateISO => zonedNowISO(timezone);
const TOMORROW_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).add(1, "day").toISOString();
const YESTERDAY_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).add(-1, "day").toISOString();

const MONTH_START_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).date(1).toISOString();
const MONTH_END_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .add(1, "month")
    .date(1)
    .subtract(1, "day")
    .toISOString();

const WEEK_START_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone).weekday(CALENDAR_RANGE.weekStartDay).toISOString();
const WEEK_END_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .weekday(CALENDAR_RANGE.weekStartDay)
    .add(CALENDAR_RANGE.weekDays, "day")
    .toISOString();

const LAST_WEEK_START_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .weekday(CALENDAR_RANGE.weekStartDay)
    .subtract(1, "week")
    .toISOString();
const LAST_WEEK_END_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .weekday(CALENDAR_RANGE.weekStartDay)
    .subtract(1, "week")
    .add(CALENDAR_RANGE.weekDays, "day")
    .toISOString();

const NEXT_7_DAYS_START_DATE = (timezone: TimeZoneEnum): TDateISO =>
  zonedNowISO(timezone);
const NEXT_7_DAYS_END_DATE = (timezone: TimeZoneEnum): TDateISO =>
  nowInTimezone(timezone)
    .add(CALENDAR_RANGE.next7DayIncrement, "day")
    .toISOString();

export const toDatesUnitNormalized = ({
  dt1,
  dt2,
  timezone,
}: {
  dt1: TDateISO;
  dt2?: TDateISO;
  timezone: TimeZoneEnum;
}): DatesUnit => {
  const date1 = startOfZonedCalendarDay({ dateISO: dt1, timezone })
    .utc()
    .toISOString();
  const date2 = endOfZonedCalendarDay({
    dateISO: dt2 ?? date1,
    timezone,
  })
    .utc()
    .toISOString();
  const dateRange: DatesUnit = { date1, date2 };
  return dateRange;
};

export const getRidesCalendarBounds = ({
  rides,
  timezone,
}: {
  rides: { rideStart: TDateISO; rideEnd: TDateISO }[];
  timezone: TimeZoneEnum;
}): DatesUnit | null => {
  if (rides.length === 0) {
    return null;
  }

  let minRideStart = rides[0].rideStart;
  let maxRideEnd = rides[0].rideEnd;

  rides.forEach(ride => {
    if (ride.rideStart < minRideStart) {
      minRideStart = ride.rideStart;
    }
    if (ride.rideEnd > maxRideEnd) {
      maxRideEnd = ride.rideEnd;
    }
  });

  return toDatesUnitNormalized({
    dt1: minRideStart,
    dt2: maxRideEnd,
    timezone,
  });
};

interface IDateUnitForCalendarModeResolverArguments {
  weekendStart: number;
  weekendDuration: number;
  timezone: TimeZoneEnum;
}

const weekendResolver = ({
  weekendStart,
  weekendDuration,
  timezone,
}: IDateUnitForCalendarModeResolverArguments): DatesUnit => {
  const weekendStartDay = nowInTimezone(timezone)
    .weekday(weekendStart)
    .toISOString();
  const weekendEndDate = nowInTimezone(timezone)
    .weekday(weekendStart)
    .add(weekendDuration - 1, "day")
    .toISOString();
  return toDatesUnitNormalized({
    dt1: weekendStartDay,
    dt2: weekendEndDate,
    timezone,
  });
};

const dateUnitForCalendarModeMap: Record<
  TopCalendarMode,
  (args: IDateUnitForCalendarModeResolverArguments) => DatesUnit
> = {
  [TopCalendarMode.TODAY]: ({ timezone }) =>
    toDatesUnitNormalized({ dt1: TODAY_DATE(timezone), timezone }),
  [TopCalendarMode.TOMORROW]: ({ timezone }) =>
    toDatesUnitNormalized({ dt1: TOMORROW_DATE(timezone), timezone }),
  [TopCalendarMode.YESTERDAY]: ({ timezone }) =>
    toDatesUnitNormalized({ dt1: YESTERDAY_DATE(timezone), timezone }),
  [TopCalendarMode.WEEKEND]: weekendResolver,
  [TopCalendarMode.WEEK]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: WEEK_START_DATE(timezone),
      dt2: WEEK_END_DATE(timezone),
      timezone,
    }),
  [TopCalendarMode.LAST_WEEK]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: LAST_WEEK_START_DATE(timezone),
      dt2: LAST_WEEK_END_DATE(timezone),
      timezone,
    }),
  [TopCalendarMode.NEXT_7_DAYS]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: NEXT_7_DAYS_START_DATE(timezone),
      dt2: NEXT_7_DAYS_END_DATE(timezone),
      timezone,
    }),
  [TopCalendarMode.MONTH]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: MONTH_START_DATE(timezone),
      dt2: MONTH_END_DATE(timezone),
      timezone,
    }),
  [TopCalendarMode.LAST_MONTH]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: LAST_MONTH_START(timezone),
      dt2: LAST_MONTH_END(timezone),
      timezone,
    }),
  [TopCalendarMode.LAST_30_DAYS]: ({ timezone }) =>
    toDatesUnitNormalized({
      dt1: LAST_30_DAYS_START(timezone),
      dt2: LAST_30_DAYS_END(timezone),
      timezone,
    }),
  [TopCalendarMode.CUSTOM]: ({ timezone }) =>
    toDatesUnitNormalized({ dt1: nowTDateISO(timezone), timezone }),
};

interface IGetDateUnitForCalendarModeArguments extends IDateUnitForCalendarModeResolverArguments {
  mode: TopCalendarMode;
}

export const getDateUnitForCalendarMode = ({
  mode,
  weekendDuration,
  weekendStart,
  timezone,
}: IGetDateUnitForCalendarModeArguments): DatesUnit => {
  const dateUnits = dateUnitForCalendarModeMap[mode]({
    weekendDuration,
    weekendStart,
    timezone,
  });
  return dateUnits;
};

const shiftCalendarDateRange = ({
  date1,
  date2,
  timezone,
  amount,
  unit,
}: {
  date1: TDateISO;
  date2: TDateISO;
  timezone: TimeZoneEnum;
  amount: number;
  unit: CalendarShiftUnit;
}): DatesUnit => {
  const shiftedDate1 = shiftZonedCalendarDate({
    dateISO: date1,
    timezone,
    amount,
    unit,
  });

  if (unit === "month") {
    const nextMonthStart = shiftZonedCalendarDate({
      dateISO: shiftedDate1,
      timezone,
      amount: 1,
      unit: "month",
    });
    const monthEnd = shiftZonedCalendarDate({
      dateISO: nextMonthStart,
      timezone,
      amount: -1,
      unit: "day",
    });

    return toDatesUnitNormalized({
      dt1: shiftedDate1,
      dt2: monthEnd,
      timezone,
    });
  }

  const shiftedDate2 = shiftZonedCalendarDate({
    dateISO: date2,
    timezone,
    amount,
    unit,
  });

  return toDatesUnitNormalized({
    dt1: shiftedDate1,
    dt2: shiftedDate2,
    timezone,
  });
};

export const calendarNext = (
  mode: TopCalendarMode,
  { date1, date2 }: DatesUnit,
  timezone: TimeZoneEnum,
): DatesUnit => {
  const { amount, unit } = CALENDAR_NAVIGATION_SHIFT[mode];
  return shiftCalendarDateRange({
    date1,
    date2,
    timezone,
    amount,
    unit,
  });
};

export const calendarPrev = (
  mode: TopCalendarMode,
  { date1, date2 }: DatesUnit,
  timezone: TimeZoneEnum,
): DatesUnit => {
  const { amount, unit } = CALENDAR_NAVIGATION_SHIFT[mode];
  return shiftCalendarDateRange({
    date1,
    date2,
    timezone,
    amount: -amount,
    unit,
  });
};

interface IGetCalendarModeForDateUnitArguments {
  dates: DatesUnit;
  weekendStart: number;
  weekendDuration: number;
  timezone: TimeZoneEnum;
}

export const getCalendarModeForDateUnit = ({
  dates,
  weekendDuration,
  weekendStart,
  timezone,
}: IGetCalendarModeForDateUnitArguments): TopCalendarMode => {
  switch (true) {
    case isEqual(
      dates,
      toDatesUnitNormalized({ dt1: TODAY_DATE(timezone), timezone }),
    ):
      return TopCalendarMode.TODAY;
    case isEqual(
      dates,
      toDatesUnitNormalized({ dt1: TOMORROW_DATE(timezone), timezone }),
    ):
      return TopCalendarMode.TOMORROW;
    case isEqual(
      dates,
      toDatesUnitNormalized({ dt1: YESTERDAY_DATE(timezone), timezone }),
    ):
      return TopCalendarMode.YESTERDAY;
    case isEqual(
      dates,
      weekendResolver({
        weekendStart,
        weekendDuration,
        timezone,
      }),
    ):
      return TopCalendarMode.WEEKEND;
    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: WEEK_START_DATE(timezone),
        dt2: WEEK_END_DATE(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.WEEK;
    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: MONTH_START_DATE(timezone),
        dt2: MONTH_END_DATE(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.MONTH;
    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: LAST_MONTH_START(timezone),
        dt2: LAST_MONTH_END(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.LAST_MONTH;

    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: NEXT_7_DAYS_START_DATE(timezone),
        dt2: NEXT_7_DAYS_END_DATE(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.NEXT_7_DAYS;

    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: LAST_WEEK_START_DATE(timezone),
        dt2: LAST_WEEK_END_DATE(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.LAST_WEEK;
    case isEqual(
      dates,
      toDatesUnitNormalized({
        dt1: LAST_30_DAYS_START(timezone),
        dt2: LAST_30_DAYS_END(timezone),
        timezone,
      }),
    ):
      return TopCalendarMode.LAST_30_DAYS;
    default:
      return TopCalendarMode.CUSTOM;
  }
};

export const storeTopCalendarData = ({
  companyId,
  dates,
  mode,
  entity,
}: {
  companyId: number;
  dates: DatesUnit;
  mode: TopCalendarMode;
  entity: RydeUiUnit;
}): boolean => {
  try {
    sessionFirstStorageHelpers.setItem(
      `${companyId}_topCalendarDates_${entity}`,
      {
        dates,
        mode,
      },
    );
    return true;
  } catch (_error) {
    return false;
  }
};

interface ITopCalendarStorageDates {
  dates?: { date1: TDateISODate; date2: TDateISODate };
  mode?: TopCalendarMode;
}

export const getStoredTopCalendarData = (
  companyId: number,
  entity: RydeUiUnit,
  timezone: TimeZoneEnum,
): {
  dates: DatesUnit;
  mode: TopCalendarMode;
} | null => {
  const data = sessionFirstStorageHelpers.getItem(
    `${companyId}_topCalendarDates_${entity}`,
  ) as ITopCalendarStorageDates | undefined;

  if (!data) {
    return null;
  }

  return {
    dates: {
      date1: (data?.dates?.date1
        ? data.dates.date1
        : nowInTimezone(timezone)) as TDateISO,
      date2: (data?.dates?.date2
        ? data.dates.date2
        : nowInTimezone(timezone)) as TDateISO,
    },
    mode: data?.mode ?? INITIAL_CALENDAR_DROPDOWN_VALUE,
  };
};
